import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Spin,
  Form,
  Card,
  Select,
  DatePicker,
  InputNumber,
  Dropdown,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import { connect } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";

import MainLayout from "../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../components/layouts/enhancement/TopContent";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";
import Settings01 from "@untitled-ui/icons-react/build/esm/Settings01";
import cnt from "../../../services/definedConstant";
import { rightAction } from "../../../redux/actions";
import {
  labelConfig,
  staticConfig,
  apiConfig,
} from "../treasuryContractBooking.config";
import { BOM } from "../../../services/definedMethod";

const STATUS_COLORS = {
  ACTIVE: { bg: "#ECFDF3", color: "#027A48" },
  PENDING: { bg: "#FFFAEB", color: "#B54708" },
  MATURED: { bg: "#EFF8FF", color: "#175CD3" },
  REJECTED: { bg: "#FEF3F2", color: "#B42318" },
  CLOSED: { bg: "#F2F4F7", color: "#344054" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_COLORS[status] || { bg: "#F2F4F7", color: "#344054" };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "4px 12px",
        borderRadius: 99,
        fontSize: 13,
        fontWeight: 600,
        background: s.bg,
        color: s.color,
      }}
    >
      {status}
    </span>
  );
};

const COUPON_FREQUENCY_OPTIONS = [
  { label: "Annual", value: "ANNUAL" },
  { label: "Semi-Annual", value: "SEMI_ANNUAL" },
  { label: "Quarterly", value: "QUARTERLY" },
  { label: "Monthly", value: "MONTHLY" },
];

const ViewBondContract = (props) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState("PENDING");
  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    if (id) fetchContractDetail(id);
  }, [id]);

  const fetchContractDetail = async (contractId) => {
    try {
      setIsLoading(true);
      // const res = await BOM.SendReq(apiConfig.getBondContractById, { id: contractId });
      const data = {
        id: 1,
        accountNo: "BOND-2026-001",
        clientId: 1,
        productId: 2,
        status: "ACTIVE",
        faceValue: 100_000_000,
        purchasePrice: 98_500_000,
        issueDate: "2026-02-24",
        maturityDate: "2031-02-24",
        tenor: 1825,
        couponRate: 13.5,
        couponFrequency: "SEMI_ANNUAL",
        settlementId: 2,
        tradeId: 2,
      };
      setStatus(data.status);
      form.setFieldsValue({
        ...data,
        issueDate: dayjs(data.issueDate),
        maturityDate: dayjs(data.maturityDate),
      });
    } catch (error) {
      BOM.AlertMsg(
        cnt.DANGER,
        error?.message || "Failed to load bond contract.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  // ── All actions are direct API calls ─────────────────────────────────────
  const runAction = async (key) => {
    try {
      setIsLoading(true);
      switch (key) {
        case "approve":
          console.log("Approve Bond Contract — id:", id);
          BOM.AlertMsg(cnt.SUCCESS, "Contract approved successfully.");
          break;
        case "reject":
          console.log("Reject Bond Contract — id:", id);
          BOM.AlertMsg(cnt.SUCCESS, "Contract rejected.");
          navigate(-1);
          break;
        case "disburse":
          console.log("Disburse Bond Contract — id:", id);
          BOM.AlertMsg(cnt.SUCCESS, "Contract disbursed successfully.");
          break;
        case "redeem_callable":
          console.log("Premature Redemption (Callable) — id:", id);
          BOM.AlertMsg(cnt.SUCCESS, "Callable redemption processed.");
          break;
        case "redeem_puttable":
          console.log("Premature Redemption (Puttable) — id:", id);
          BOM.AlertMsg(cnt.SUCCESS, "Puttable redemption processed.");
          break;
        case "get_transactions":
          console.log("Get Contract Transactions — id:", id);
          break;
        case "get_transaction_detail":
          console.log("Get Transaction Detail — id:", id);
          break;
        default:
          break;
      }
    } catch (error) {
      BOM.AlertMsg(cnt.DANGER, error?.message || "Action failed.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAction = ({ key }) => runAction(key);

  const actionMenuItems = [
    { key: "approve", label: "Approve Contract" },
    { key: "reject", label: "Reject Contract" },
    { type: "divider" },
    { key: "disburse", label: "Disburse Contract" },
    { key: "redeem_callable", label: "Premature Redemption (Callable)" },
    { key: "redeem_puttable", label: "Premature Redemption (Puttable)" },
    { type: "divider" },
    { key: "get_transactions", label: "Get Contract Transactions" },
    { key: "get_transaction_detail", label: "Get Transaction Detail" },
  ];

  const ds = { height: 44, background: "#F9FAFB", cursor: "not-allowed" };

  const Tip = ({ text, tip }) => (
    <span>
      {text}
      <Tooltip title={tip}>
        <InfoCircleOutlined style={{ marginLeft: 4, color: "#667085" }} />
      </Tooltip>
    </span>
  );

  return (
    <MainLayout pageItems={staticConfig.viewBondContract}>
      {/* <TopContent pageTitle={labelConfig.bondContract} /> */}

      <Spin spinning={isLoading} size="large">
        <div style={{ padding: "24px" }}>
          <Card
            style={{
              boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
              borderRadius: 20,
            }}
          >
            {/* Header */}
            <Row
              justify="space-between"
              align="middle"
              style={{ marginBottom: 24 }}
            >
              <Col style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <h3 style={{ margin: 0, fontWeight: 600 }}>
                  Bond Contract Details
                </h3>
                {/* <StatusBadge status={status} /> */}
              </Col>
              <Col>
                <Dropdown
                  menu={{ items: actionMenuItems, onClick: handleAction }}
                  trigger={["click"]}
                  placement="bottomRight"
                >
                  <button
                    type="button"
                    style={{
                      height: 44,
                      padding: "0 20px",
                      borderRadius: 8,
                      border: "1px solid #D0D5DD",
                      background: "#fff",
                      color: "#344054",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <Settings01 width={16} height={16} color="#344054" />
                    Actions
                    <ChevronDown width={16} height={16} color="#667085" />
                  </button>
                </Dropdown>
              </Col>
            </Row>

            <Form layout="vertical" form={form}>
              {/* Account No & Client ID */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Account No"
                        tip="The unique account number for this bond contract."
                      />
                    }
                    name="accountNo"
                  >
                    <InputNumber style={{ width: "100%", ...ds }} disabled />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Client ID"
                        tip="The client this bond contract belongs to."
                      />
                    }
                    name="clientId"
                  >
                    <InputNumber style={{ width: "100%", ...ds }} disabled />
                  </Form.Item>
                </Col>
              </Row>

              {/* Product ID & Trade ID */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Product ID"
                        tip="The bond product linked to this contract."
                      />
                    }
                    name="productId"
                  >
                    <InputNumber style={{ width: "100%", ...ds }} disabled />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Trade ID"
                        tip="The trade this contract is linked to."
                      />
                    }
                    name="tradeId"
                  >
                    <InputNumber style={{ width: "100%", ...ds }} disabled />
                  </Form.Item>
                </Col>
              </Row>

              {/* Settlement ID & Face Value */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Settlement ID"
                        tip="The settlement record associated with this contract."
                      />
                    }
                    name="settlementId"
                  >
                    <InputNumber style={{ width: "100%", ...ds }} disabled />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Face Value"
                        tip="The nominal / par value of the bond."
                      />
                    }
                    name="faceValue"
                  >
                    <InputNumber
                      style={{ width: "100%", ...ds }}
                      formatter={(v) =>
                        v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                      }
                      parser={(v) => v?.replace(/,/g, "")}
                      precision={2}
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Purchase Price & Coupon Rate */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Purchase Price"
                        tip="The actual price paid for the bond."
                      />
                    }
                    name="purchasePrice"
                  >
                    <InputNumber
                      style={{ width: "100%", ...ds }}
                      formatter={(v) =>
                        v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                      }
                      parser={(v) => v?.replace(/,/g, "")}
                      precision={2}
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Coupon Rate (%)"
                        tip="The annual coupon interest rate paid by the bond."
                      />
                    }
                    name="couponRate"
                  >
                    <InputNumber
                      style={{ width: "100%", ...ds }}
                      precision={2}
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Coupon Frequency & Tenor */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Coupon Frequency"
                        tip="How often coupon payments are made."
                      />
                    }
                    name="couponFrequency"
                  >
                    <Select
                      style={ds}
                      suffixIcon={<ChevronDown color="#667085" />}
                      options={COUPON_FREQUENCY_OPTIONS}
                      placeholder="—"
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Tenor (Days)"
                        tip="The number of days from issue to maturity."
                      />
                    }
                    name="tenor"
                  >
                    <InputNumber
                      style={{ width: "100%", ...ds }}
                      precision={0}
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Issue Date & Maturity Date */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Issue Date"
                        tip="The date the bond was issued."
                      />
                    }
                    name="issueDate"
                  >
                    <DatePicker
                      style={{
                        width: "100%",
                        height: 44,
                        background: "#F9FAFB",
                        cursor: "not-allowed",
                      }}
                      format="YYYY-MM-DD"
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Maturity Date"
                        tip="The date the bond matures."
                      />
                    }
                    name="maturityDate"
                  >
                    <DatePicker
                      style={{
                        width: "100%",
                        height: 44,
                        background: "#F9FAFB",
                        cursor: "not-allowed",
                      }}
                      format="YYYY-MM-DD"
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>

              <div
                style={{ borderTop: "1px solid #EAECF0", margin: "8px 0 20px" }}
              />

              <Row justify="end">
                <Col>
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    style={{
                      height: 44,
                      padding: "0 24px",
                      borderRadius: 8,
                      border: "1px solid #D0D5DD",
                      background: "#fff",
                      color: "#344054",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                    }}
                  >
                    Back
                  </button>
                </Col>
              </Row>
            </Form>
          </Card>
        </div>
      </Spin>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({ rightBtn: state.RightSideBtn });
export default connect(mapStateToProps, { rightAction })(ViewBondContract);
